import { useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { now, toISO } from 'lib/utils/date';
import Routes from 'lib/utils/routes';
import { throwCriticalErrorToast, throwWarningToasts } from 'lib/utils/toast';
import { bugsnag } from 'lib/external/bugsnag';
import {
  START_ACTIVITY_WITH_LABOR_TICKET_NATS,
  SUBMIT_LABOR_TICKET_TRANSACTION,
} from 'lib/api/mutations';
import {
  actionSetRecentOperatorRuns,
  actionSetLaborTicketActionLoading,
  actionSetOperatorIsLoggingOut,
  actionOpenClockOutModal,
} from 'lib/actions';
import { getMachine } from 'lib/selectors/getMachine';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { getLatestOperatorRun } from 'lib/selectors/getLatestOperatorRun';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getClockOutFlowEnabled } from 'lib/selectors/getClockOutFlowEnabled';
import { getShiftHandoverEnabled } from 'lib/selectors/getShiftHandoverEnabled';
import { getBugsnagUserInfo } from 'lib/selectors/getBugsnagUserInfo';

/**
 * Ends the current operator run through the labor-ticket submit pipeline
 * (submit-nats when postLaborUpdatesOnNats is set, submit otherwise) rather than
 * the legacy /operator-view/operator-run/stop endpoint. The request carries only
 * a stopOperatorRun action — no labor ticket is opened or closed.
 *
 * This backs the "keep-open" sign-out: the latest labor ticket is already CLOSED
 * but the operator is still signed in, and the production run must be left open
 * for the next operator. There is no ticket to reconcile, so no form is shown.
 *
 * Sign-out is reflected optimistically (mirrors useOperatorLogout); the operator
 * run subscription reconciles the authoritative state shortly after.
 *
 * @param {Function} [onComplete] - Called after a successful stop.
 * @returns {Function} Async trigger that ends the current operator run.
 */
const useStopOperatorRun = (onComplete = () => {}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const natsFlowEnabled = useSelector(getPostLaborUpdatesOnNats);
  const latestOperatorRun = useSelector(getLatestOperatorRun);
  const currentOperator = useSelector(getCurrentOperator);
  const clockOutFlowEnabled = useSelector(getClockOutFlowEnabled);
  const shiftHandoverEnabled = useSelector(getShiftHandoverEnabled);
  const bugsnagUserInfo = useSelector(getBugsnagUserInfo);

  const mutationDoc = natsFlowEnabled
    ? START_ACTIVITY_WITH_LABOR_TICKET_NATS
    : SUBMIT_LABOR_TICKET_TRANSACTION;

  const clearLoading = () => {
    dispatch(actionSetLaborTicketActionLoading(false));
    dispatch(actionSetOperatorIsLoggingOut(false));
  };

  const [submitStop, { loading }] = useMutation(mutationDoc, {
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      // submit-nats returns { success, ... }; treat a non-success response as a
      // failed sign-out and leave the operator signed in.
      if (natsFlowEnabled) {
        const response = res?.startActivityWithLaborTicketResponse;
        if (!response?.success) {
          clearLoading();
          throwCriticalErrorToast(
            response?.error || response?.message || 'Could not sign out'
          );
          return;
        }
        // Non-fatal ERP warnings (e.g. Epicor BPM InfoMessages) on sign-out.
        throwWarningToasts(response.warnings);
      }

      // Reflect the sign-out immediately by ending the latest run; the operator
      // run subscription reconciles the authoritative state shortly after.
      if (latestOperatorRun) {
        dispatch(
          actionSetRecentOperatorRuns({
            operatorRuns: [
              { ...latestOperatorRun, endAt: toISO(now().valueOf()) },
            ],
          })
        );
      }

      if (clockOutFlowEnabled || shiftHandoverEnabled) {
        dispatch(actionOpenClockOutModal(currentOperator?.erpId));
      }

      clearLoading();
      history.push(Routes.machineIdHomePath(machine.id));
      onComplete();
    },
    onError: (err) => {
      bugsnag.notify(err, (event) => {
        event.addMetadata('user', bugsnagUserInfo);
      });
      clearLoading();
      throwCriticalErrorToast(`Could not sign out: ${err}`);
    },
  });

  return async () => {
    if (loading) {
      return;
    }
    dispatch(actionSetLaborTicketActionLoading(true));
    dispatch(actionSetOperatorIsLoggingOut(true));

    await submitStop({
      variables: {
        input: {
          stopOperatorRun: {
            machineId: machine.id,
            endAt: toISO(now().valueOf()),
          },
        },
      },
    });
  };
};

export default useStopOperatorRun;
